import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import TextInput from 'src/components/UI/input/TextInput';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { EVENTS } from 'src/components/admin/appReducer';
import { stripMemberReferencesToPid } from '../../pedigree/edgeIntegrity';
import {
  deleteConsequences,
  pathwayNote,
  PATHWAY_NOTE_UNKNOWN,
  CHILDREN_KEPT_NOTE
} from '../../pedigree/deleteConsequences';

function DeleteNode({
  node,
  user,
  memberRecords,
  setMemberRecords,
  handleClose
}) {
  const [inputValue, setInputValue] = React.useState('');
  const [error, setError] = React.useState(false);
  const { setSnack } = React.useContext(SnackContext);
  const { dispatchUpdate } = React.useContext(AppContext);

  const onChange = (e) => {
    setInputValue(e.target.value);
    setError(false);
  };

  const isStructuralProband = !node.familyMemberId && !!node.proBandId;

  // What the deletion takes with it. NOTHING CASCADES — the children survive and
  // are orphaned — which is exactly what this dialog never said before
  // TGCLOUD-1644. Lines that would read "0" are already filtered out, so a leaf
  // node shows no bullets at all rather than a list of zeroes.
  const consequences = React.useMemo(
    () => deleteConsequences(node, memberRecords),
    [node, memberRecords]
  );

  // Care pathways are the one consequence with no client-side source, so the
  // dialog asks. 'pending' shows nothing at all — a line that appears a beat
  // after the dialog opens would be worse than one that is simply absent — and
  // 'unknown' (the request failed) falls back to the old unconditional wording,
  // because failing to warn about a real deletion is worse than a moment's pause.
  const [pathwayCount, setPathwayCount] = React.useState('pending');
  const pathwayRef = node.familyMemberId || node.proBandId;

  React.useEffect(() => {
    if (!pathwayRef) {
      setPathwayCount(0);
      return;
    }
    let live = true;
    axios
      .get(APICONSTANTS.pedigreePathwayCount.replace(':member_id', pathwayRef))
      .then(({ data }) => live && setPathwayCount(data?.total ?? 0))
      .catch(() => live && setPathwayCount('unknown'));
    return () => {
      live = false;
    };
  }, [pathwayRef]);

  const pathwayLine =
    pathwayCount === 'pending'
      ? null
      : pathwayCount === 'unknown'
      ? PATHWAY_NOTE_UNKNOWN
      : pathwayCount > 0
      ? pathwayNote(pathwayCount)
      : null;

  // Nothing to warn about — a leaf with no pathway records gets no block at all,
  // rather than a reassuring-sounding box that trains people to skip it.
  const hasConsequences = consequences.lines.length > 0 || !!pathwayLine;

  const proceedToDelete = async () => {
    const displayedUpn = String(node.upn || node.pid);
    if (String(inputValue).trim() !== displayedUpn) {
      setError(true);
      return;
    }

    const pid = node.pid;

    try {
      if (isStructuralProband) {
        await axios.post(
          APICONSTANTS.deleteStructuralProband.replace(':user_id', user._id),
          { pid }
        );
        const { data } = await axios.get(
          APICONSTANTS.getPedigreeDetails.replace(':familyId', user.familyId)
        );
        dispatchUpdate({
          type: EVENTS.SET_PEDIGREE_SELECTED_USER,
          value: { family: data.families, selectedUser: data.proband }
        });
        handleClose();
        return;
      }

      await axios.post(
        APICONSTANTS.deletePedigreeFamilyMember
          .replace(':user_id', user._id)
          .replace(':member_id', node.familyMemberId)
      );

      // Functional update: state may have moved on while the POST was in
      // flight (save-echo identity backfill, concurrent edits) — derive the
      // deletion from `prev`, never from the render-time snapshot.
      setMemberRecords((prev) =>
        prev
          .filter((p) => p.pid !== pid)
          .map((p) => stripMemberReferencesToPid(p, pid))
      );
      handleClose();
    } catch {
      setSnack({
        status: true,
        msg: 'Failed to delete family member',
        severity: 'error'
      });
    }
  };

  return (
    <CustomDialog
      open={true}
      handleClose={handleClose}
      showActions={true}
      saveBtnLabel={'Confirm Delete'}
      title={'Delete Confirmation'}
      titleAppearance={'warning'}
      minHeight={200}
      onSave={proceedToDelete}
      allowBackgroundClose={handleClose}
    >
      <Box>
        <Typography
          sx={{
            fontWeight: 'bold'
          }}
        >
          {`Warning: This action will permanently delete the following family member's details, including all associated records and data.`}
        </Typography>
        <Box
          sx={{
            py: 2
          }}
        >
          <Typography>UPN : {node.upn || node.pid}</Typography>
          <Typography>
            Full Name: {node.firstName} {node.lastName}
          </Typography>
          <Typography>
            Family Member Id : {node.familyMemberId || node.proBandId}
          </Typography>
          <Typography>Date of Birth: {node.dob}</Typography>
        </Box>
        {hasConsequences && (
          <Box
            sx={{
              p: 1.5,
              mb: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'warning.main',
              bgcolor: (t) => alpha(t.palette.warning.main, 0.06)
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {consequences.lines.length
                ? 'Removing them will also:'
                : 'Also note:'}
            </Typography>
            {consequences.lines.length > 0 && (
              <Box
                component="ul"
                sx={{ m: 0, mt: 0.5, pl: 2.5, '& li': { mb: 0.25 } }}
              >
                {consequences.lines.map((line) => (
                  <li key={line}>
                    <Typography variant="body2" component="span">
                      {line}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
            {pathwayLine && (
              <Typography variant="body2" sx={{ mt: 0.75 }}>
                {pathwayLine}
              </Typography>
            )}
            {consequences.childCount > 0 && (
              <Typography
                variant="body2"
                sx={{ mt: 0.75, color: 'text.secondary' }}
              >
                {CHILDREN_KEPT_NOTE}
              </Typography>
            )}
          </Box>
        )}
        <Typography variant="subtitle2">
          Please note that this action cannot be undone, and all historical
          information linked to this family member will be permanently removed
          from the system.
        </Typography>
        {isStructuralProband && (
          <Typography
            variant="subtitle2"
            sx={{
              color: 'warning.main',
              pt: 1
            }}
          >
            This is the original proband. Deleting them will promote another
            family member to be the new family root. The pedigree will refresh
            after deletion.
          </Typography>
        )}
        <Box
          className="user-form"
          sx={{
            pt: 2
          }}
        >
          <TextInput
            label="Enter Family Member UPN to confirm deletion"
            error={error}
            helperText={error && 'Invalid Family Member UPN'}
            placeholder={String(node.upn || node.pid)}
            onChange={onChange}
          />
        </Box>
      </Box>
    </CustomDialog>
  );
}

export default DeleteNode;
